import { useState, type ReactNode } from "react"
import { useFailures, useOpenRequests } from "../store/derived"
import { setUi, useStore } from "../store/store"
import { AgentsList } from "./AgentsSection"
import { FailuresList, RequestsBoard } from "./RequestsSection"

function useCollapsed(key: string): [boolean, () => void] {
	const k = `walkie.sec.${key}`
	const [v, setV] = useState(() => {
		try {
			return localStorage.getItem(k) === "1"
		} catch {
			return false
		}
	})
	return [
		v,
		() => {
			setV(!v)
			try {
				localStorage.setItem(k, v ? "0" : "1")
			} catch {
				/* ignore */
			}
		},
	]
}

function Section(props: {
	id: string
	title: string
	count: ReactNode
	className: string
	extra?: ReactNode
	children: ReactNode
}) {
	const [collapsed, toggle] = useCollapsed(props.id)
	return (
		<section
			className={"sec " + props.className + (collapsed ? " collapsed" : "")}
			aria-label={props.title}
		>
			<div className="sec-h">
				<button className="sec-toggle" aria-expanded={!collapsed} onClick={toggle}>
					<span className="caret" aria-hidden>
						▼
					</span>
					{props.title}
					{props.count}
				</button>
				<span className="grow" />
				{props.extra}
			</div>
			{!collapsed && (
				<div className="sec-body" id={`sec-${props.id}`}>
					{props.children}
				</div>
			)}
		</section>
	)
}

export function RightPanel() {
	const agents = useStore(s => s.agents)
	const open = useOpenRequests()
	const failures = useFailures()
	const list = Object.values(agents)
	const live = list.filter(a => a.state === "active" || a.state === "idle").length
	const blockers = open.filter(m => m.kind === "blocker").length
	return (
		<aside className="side" aria-label="Agents, requests and delivery failures">
			<Section
				id="agents"
				title="Agents"
				className="agents-sec"
				count={
					<span className="count" aria-label={`${live} live of ${list.length}`}>
						{live}/{list.length}
					</span>
				}
				extra={
					<button
						className="btn btn-xs btn-ghost"
						onClick={() => setUi({ connect: true })}
					>
						+ Connect
					</button>
				}
			>
				<AgentsList />
			</Section>
			<Section
				id="requests"
				title="Requests"
				className="req-sec"
				count={
					<>
						<span className="count">{open.length}</span>
						{blockers > 0 && <span className="count bad">{blockers} blocking</span>}
					</>
				}
			>
				<RequestsBoard />
			</Section>
			<Section
				id="failures"
				title="Delivery failures"
				className="fail-sec"
				count={
					<span className={"count" + (failures.length ? " bad" : "")}>
						{failures.length}
					</span>
				}
			>
				<FailuresList />
			</Section>
		</aside>
	)
}
